import log from 'loglevel';

// Define our own LogLevel type for better type safety
type LogLevelString = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent';

// Define logger interface for type safety
interface Logger {
  trace: (message: string, ...args: any[]) => void;
  debug: (message: string, ...args: any[]) => void;
  info: (message: string, ...args: any[]) => void;
  warn: (message: string, ...args: any[]) => void;
  error: (message: string, ...args: any[]) => void;
}

// Type definitions for logging data
interface LogData {
  [key: string]: any;
}

interface ApiLogData extends LogData {
  data?: any;
}

interface ApiResponseLogData extends LogData {
  status: string | number;
  duration?: string;
}

interface ApiErrorLogData extends LogData {
  message: string;
  status?: string | number;
  stack?: string;
}

interface ChatMessageLogData extends LogData {
  message: string;
  [key: string]: any;
}

interface AuthActionLogData extends LogData {
  success: boolean;
  [key: string]: any;
}

interface UIActionLogData extends LogData {
  [key: string]: any;
}

interface PerformanceLogData extends LogData {
  duration: string;
  [key: string]: any;
}

// Simple environment detection
const isDevelopment = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.includes('dev')
);

// Get log level from environment or use default
function getLogLevel(): LogLevelString {
  try {
    // Get from Vite environment
    const envLevel = import.meta.env.VITE_LOG_LEVEL as string;
    console.log('🔍 Logger: Environment log level from VITE_LOG_LEVEL:', envLevel);
    
    if (envLevel) {
      switch (envLevel.toLowerCase()) {
        case 'trace':
          return 'trace';
        case 'debug':
          return 'debug';
        case 'info':
          return 'info';
        case 'warn':
          return 'warn';
        case 'error':
          return 'error';
        case 'silent':
          return 'silent';
        default:
          console.warn('🔍 Logger: Unknown log level:', envLevel, 'using default');
          return isDevelopment ? 'debug' : 'warn';
      }
    } else {
      console.log('🔍 Logger: No VITE_LOG_LEVEL found, using default');
      return isDevelopment ? 'debug' : 'warn';
    }
  } catch (error) {
    console.warn('🔍 Logger: Failed to read VITE_LOG_LEVEL:', error);
    return isDevelopment ? 'debug' : 'warn';
  }
}

const logLevel = getLogLevel();
console.log('🔍 Logger: Setting log level to:', logLevel, 'isDevelopment:', isDevelopment);

// Set the global log level
log.setLevel(logLevel);

// Create custom loggers for different modules
const createLogger = (name: string): Logger => {
  const logger = log.getLogger(name);
  logger.setLevel(logLevel);

  // Add custom formatting for better readability
  const originalFactory = logger.methodFactory;
  logger.methodFactory = function (methodName: any, logLevel: any, loggerName: string | symbol) {
    const rawMethod = originalFactory(methodName, logLevel, loggerName);

    return function (message: string, ...args: any[]) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [${String(loggerName)}] [${String(methodName).toUpperCase()}]`;
      rawMethod(prefix, message, ...args);
    };
  };

  // Rebuild the logger to apply the new method factory
  logger.rebuild();

  return logger as Logger;
};

// Export pre-configured loggers for different modules
export const logger: Logger = log as Logger;
export const apiLogger: Logger = createLogger('API');
export const chatLogger: Logger = createLogger('CHAT');
export const authLogger: Logger = createLogger('AUTH');
export const uiLogger: Logger = createLogger('UI');
export const deployLogger: Logger = createLogger('DEPLOY');

// Export the createLogger function for custom loggers
export { createLogger };

// Log the initialization
console.log('🔍 Logger: System initialized with level:', logLevel);
logger.info('Logger system initialized', { 
  level: logLevel, 
  isDevelopment,
  envLevel: import.meta.env.VITE_LOG_LEVEL 
});

// Utility functions for common logging patterns
export const logApiCall = (method: string, url: string, data: ApiLogData | null = null): void => {
  apiLogger.debug(`${method} ${url}`, data ? { data } : '');
};

export const logApiResponse = (method: string, url: string, response: { status?: string | number }, duration: number | null = null): void => {
  const logData: ApiResponseLogData = { 
    status: response.status || 'unknown',
    ...(duration && { duration: `${duration}ms` })
  };
  apiLogger.debug(`${method} ${url} - Response`, logData);
};

export const logApiError = (method: string, url: string, error: any): void => {
  const errorData: ApiErrorLogData = {
    message: error?.message || 'Unknown error',
    status: error?.status || error?.response?.status,
    stack: isDevelopment ? error?.stack : undefined
  };
  apiLogger.error(`${method} ${url} - Error`, errorData);
};

export const logChatMessage = (type: string, message: string, metadata: Partial<ChatMessageLogData> = {}): void => {
  const logData: ChatMessageLogData = { message, ...metadata };
  chatLogger.debug(`Chat ${type}`, logData);
};

export const logAuthAction = (action: string, success: boolean, details: Partial<AuthActionLogData> = {}): void => {
  const logData: AuthActionLogData = { success, ...details };
  const level = success ? 'info' : 'warn';
  authLogger[level](`Auth ${action}`, logData);
};

export const logUIAction = (component: string, action: string, data: UIActionLogData = {}): void => {
  uiLogger.debug(`${component} - ${action}`, data);
};

// Performance logging utilities
export const createPerformanceLogger = (operation: string) => {
  const start = performance.now();

  return {
    end: (details: LogData = {}): number => {
      const duration = Math.round(performance.now() - start);
      const perfData: PerformanceLogData = { duration: `${duration}ms`, ...details };
      logger.debug(`Performance: ${operation}`, perfData);
      return duration;
    }
  };
};

export default logger;
